import React, { useState, useRef } from 'react';
import { importUrl } from '../services/api';
import AdminHeader from './AdminHeader';

const STATUS = {
  PENDING: 'pending',
  RUNNING: 'running',
  SUCCESS: 'success',
  ERROR: 'error',
};

const statusIcon = {
  [STATUS.PENDING]: '⏳',
  [STATUS.RUNNING]: '⚙️',
  [STATUS.SUCCESS]: '✅',
  [STATUS.ERROR]: '❌',
};

const statusColor = {
  [STATUS.PENDING]: '#888',
  [STATUS.RUNNING]: '#1976D2',
  [STATUS.SUCCESS]: '#4CAF50',
  [STATUS.ERROR]: '#e53935',
};

function ImportUrlsPage() {
  const [urlsText, setUrlsText] = useState('');
  const [enableRewrite, setEnableRewrite] = useState(true);
  const [showOptions, setShowOptions] = useState(false);
  const [rewriteOptions, setRewriteOptions] = useState({
    imageCount: 1,
    includeSummary: true,
    contentStyle: 'professional',
    imageStyle: 'realistic',
    useOriginalImages: false,
    imageSource: 'dalle',
  });
  const [results, setResults] = useState([]); // { url, status, title, section, error }
  const [running, setRunning] = useState(false);
  const [summary, setSummary] = useState(null);
  const abortRef = useRef(false);

  const parseUrls = (text) =>
    text
      .split('\n')
      .map((u) => u.trim())
      .filter((u) => {
        if (!u) return false;
        try { new URL(u); return true; } catch { return false; }
      });

  const handleStart = async () => {
    const urls = parseUrls(urlsText);
    if (urls.length === 0) {
      alert('Please enter at least one valid URL (one per line).');
      return;
    }

    abortRef.current = false;
    setRunning(true);
    setSummary(null);

    // Initialise result rows
    const initial = urls.map((url) => ({ url, status: STATUS.PENDING, title: '', section: '', error: '' }));
    setResults(initial);

    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < urls.length; i++) {
      if (abortRef.current) break;

      // Mark as running
      setResults((prev) => {
        const next = [...prev];
        next[i] = { ...next[i], status: STATUS.RUNNING };
        return next;
      });

      try {
        const response = await importUrl(urls[i], enableRewrite, rewriteOptions);
        if (response.success) {
          successCount++;
          setResults((prev) => {
            const next = [...prev];
            next[i] = {
              ...next[i],
              status: STATUS.SUCCESS,
              title: response.data?.title || '',
              section: response.data?.section || '',
            };
            return next;
          });
        } else {
          errorCount++;
          setResults((prev) => {
            const next = [...prev];
            next[i] = { ...next[i], status: STATUS.ERROR, error: response.message || 'Unknown error' };
            return next;
          });
        }
      } catch (err) {
        errorCount++;
        const msg = err?.response?.data?.message || err.message || 'Request failed';
        setResults((prev) => {
          const next = [...prev];
          next[i] = { ...next[i], status: STATUS.ERROR, error: msg };
          return next;
        });
      }
    }

    setRunning(false);
    setSummary({ total: urls.length, success: successCount, error: errorCount });
  };

  const handleStop = () => {
    abortRef.current = true;
  };

  const handleClear = () => {
    setResults([]);
    setSummary(null);
    setUrlsText('');
  };

  const parsedCount = parseUrls(urlsText).length;

  return (
    <div className="app-wrapper">
      <AdminHeader
        title="Import URLs"
        subtitle="Bulk-import articles from external URLs into the database"
      />
    <div className="app">

      <div style={{ margin: '0 auto' }}>
        {/* URL Input */}
        <div
          style={{
            background: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          }}
        >
          <h2 style={{ margin: '0 0 12px 0', fontSize: '1.1em', color: '#333' }}>
            📋 Paste URLs to Import
          </h2>
          <p style={{ margin: '0 0 10px 0', fontSize: '0.85em', color: '#666' }}>
            One URL per line. Invalid lines are silently ignored.
          </p>
          <textarea
            value={urlsText}
            onChange={(e) => setUrlsText(e.target.value)}
            disabled={running}
            placeholder={
              'https://example.com/article-one\nhttps://example.com/article-two\nhttps://example.com/article-three'
            }
            rows={8}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '10px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '0.9em',
              fontFamily: 'monospace',
              resize: 'vertical',
              background: running ? '#f9f9f9' : '#fff',
            }}
          />
          {parsedCount > 0 && (
            <div style={{ fontSize: '0.82em', color: '#555', marginTop: '6px' }}>
              {parsedCount} valid URL{parsedCount !== 1 ? 's' : ''} detected
            </div>
          )}
        </div>

        {/* Rewriting Options */}
        <div
          style={{
            background: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            padding: '16px 20px',
            marginBottom: '20px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9em', color: '#555' }}>
              <input
                type="checkbox"
                checked={enableRewrite}
                onChange={(e) => setEnableRewrite(e.target.checked)}
                disabled={running}
              />
              <span>✨ Enable AI Rewriting</span>
            </label>
            {enableRewrite && (
              <button
                onClick={() => setShowOptions((v) => !v)}
                disabled={running}
                style={{
                  background: 'none',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '4px 12px',
                  cursor: 'pointer',
                  fontSize: '0.85em',
                  color: '#555',
                }}
              >
                ⚙️ {showOptions ? 'Hide' : 'Show'} Options
              </button>
            )}
          </div>

          {enableRewrite && showOptions && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px',
                marginTop: '14px',
                background: '#f8f9fa',
                border: '1px solid #e8e8e8',
                borderRadius: '6px',
                padding: '14px',
              }}
            >
              {/* Image count */}
              <div>
                <label style={{ display: 'block', fontSize: '0.85em', color: '#555', marginBottom: '5px' }}>
                  📷 Images per article
                </label>
                <input
                  type="range"
                  min="0"
                  max="5"
                  value={rewriteOptions.imageCount}
                  onChange={(e) => setRewriteOptions({ ...rewriteOptions, imageCount: parseInt(e.target.value) })}
                  disabled={running}
                  style={{ width: '100%' }}
                />
                <div style={{ fontSize: '0.8em', color: '#777', marginTop: '3px' }}>
                  {rewriteOptions.imageCount} {rewriteOptions.imageCount === 1 ? 'image' : 'images'}
                </div>
              </div>

              {/* Use original images */}
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.85em', color: '#555' }}>
                  <input
                    type="checkbox"
                    checked={rewriteOptions.useOriginalImages}
                    onChange={(e) => setRewriteOptions({ ...rewriteOptions, useOriginalImages: e.target.checked })}
                    disabled={running}
                  />
                  <span>🖼️ Use Original Article Images</span>
                </label>
              </div>

              {/* Include summary */}
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.85em', color: '#555' }}>
                  <input
                    type="checkbox"
                    checked={rewriteOptions.includeSummary}
                    onChange={(e) => setRewriteOptions({ ...rewriteOptions, includeSummary: e.target.checked })}
                    disabled={running}
                  />
                  <span>💡 Include Summary</span>
                </label>
              </div>

              {/* Writing style */}
              <div>
                <label style={{ display: 'block', fontSize: '0.85em', color: '#555', marginBottom: '5px' }}>
                  ✍️ Writing Style
                </label>
                <select
                  value={rewriteOptions.contentStyle}
                  onChange={(e) => setRewriteOptions({ ...rewriteOptions, contentStyle: e.target.value })}
                  disabled={running}
                  style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.85em' }}
                >
                  <option value="professional">Professional</option>
                  <option value="casual">Casual & Friendly</option>
                  <option value="technical">Technical & Detailed</option>
                  <option value="simple">Simple & Easy to Read</option>
                </select>
              </div>

              {/* Image style */}
              <div>
                <label style={{ display: 'block', fontSize: '0.85em', color: '#555', marginBottom: '5px' }}>
                  🎨 Image Style
                </label>
                <select
                  value={rewriteOptions.imageStyle}
                  onChange={(e) => setRewriteOptions({ ...rewriteOptions, imageStyle: e.target.value })}
                  disabled={running}
                  style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.85em' }}
                >
                  <option value="realistic">Realistic Photos</option>
                  <option value="illustration">Illustrations</option>
                  <option value="minimal">Minimal & Clean</option>
                  <option value="vibrant">Vibrant & Colorful</option>
                </select>
              </div>

              {/* Image source */}
              <div>
                <label style={{ display: 'block', fontSize: '0.85em', color: '#555', marginBottom: '5px' }}>
                  🌐 Image Source
                </label>
                <select
                  value={rewriteOptions.imageSource}
                  onChange={(e) => setRewriteOptions({ ...rewriteOptions, imageSource: e.target.value })}
                  disabled={running}
                  style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.85em' }}
                >
                  <option value="dalle">🎨 DALL-E (OpenAI)</option>
                  <option value="gemini">✨ Gemini (Google AI)</option>
                </select>
                <div style={{ fontSize: '0.75em', color: '#777', marginTop: '4px' }}>
                  Choose which AI generates the images
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '24px' }}>
          {!running ? (
            <button
              className="btn btn-primary"
              onClick={handleStart}
              disabled={parsedCount === 0}
              style={{ padding: '10px 28px', fontSize: '1em' }}
            >
              🚀 Start Import ({parsedCount} URL{parsedCount !== 1 ? 's' : ''})
            </button>
          ) : (
            <button
              className="btn btn-secondary"
              onClick={handleStop}
              style={{ padding: '10px 28px', fontSize: '1em', backgroundColor: '#e53935', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              ⏹ Stop
            </button>
          )}
          {results.length > 0 && !running && (
            <button
              className="btn btn-secondary"
              onClick={handleClear}
              style={{ padding: '10px 20px' }}
            >
              🗑 Clear
            </button>
          )}
        </div>

        {/* Summary Banner */}
        {summary && (
          <div
            style={{
              padding: '12px 18px',
              borderRadius: '6px',
              marginBottom: '16px',
              backgroundColor: summary.error === 0 ? '#e8f5e9' : '#fff8e1',
              border: `1px solid ${summary.error === 0 ? '#a5d6a7' : '#ffe082'}`,
              color: '#333',
              fontSize: '0.95em',
            }}
          >
            Import complete — {summary.success} succeeded, {summary.error} failed out of {summary.total} total.
          </div>
        )}

        {/* Results Table */}
        {results.length > 0 && (
          <div
            style={{
              background: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            }}
          >
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88em' }}>
              <thead>
                <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #e0e0e0' }}>
                  <th style={{ padding: '10px 14px', textAlign: 'left', width: '36px' }}>#</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left' }}>URL</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left', width: '180px' }}>Title</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left', width: '110px' }}>Section</th>
                  <th style={{ padding: '10px 14px', textAlign: 'center', width: '80px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, i) => (
                  <tr
                    key={r.url + i}
                    style={{
                      borderBottom: '1px solid #f0f0f0',
                      background: r.status === STATUS.RUNNING ? '#e3f2fd' : 'transparent',
                    }}
                  >
                    <td style={{ padding: '9px 14px', color: '#999' }}>{i + 1}</td>
                    <td style={{ padding: '9px 14px', wordBreak: 'break-all', maxWidth: '300px' }}>
                      <a href={r.url} target="_blank" rel="noopener noreferrer" style={{ color: '#1976D2', textDecoration: 'none' }}>
                        {r.url.length > 60 ? r.url.slice(0, 60) + '…' : r.url}
                      </a>
                    </td>
                    <td style={{ padding: '9px 14px', color: '#444' }}>
                      {r.status === STATUS.RUNNING ? (
                        <em style={{ color: '#1976D2' }}>processing…</em>
                      ) : r.status === STATUS.ERROR ? (
                        <span style={{ color: '#e53935', fontSize: '0.85em' }}>{r.error}</span>
                      ) : (
                        r.title || '—'
                      )}
                    </td>
                    <td style={{ padding: '9px 14px', color: '#555', fontSize: '0.85em' }}>
                      {r.section ? (
                        <span
                          style={{
                            background: r.section === 'featured' ? '#e3f2fd' : '#f3e5f5',
                            color: r.section === 'featured' ? '#1565c0' : '#6a1b9a',
                            padding: '2px 8px',
                            borderRadius: '12px',
                          }}
                        >
                          {r.section}
                        </span>
                      ) : '—'}
                    </td>
                    <td style={{ padding: '9px 14px', textAlign: 'center' }}>
                      <span
                        title={r.status}
                        style={{ fontSize: '1.1em', color: statusColor[r.status] }}
                      >
                        {statusIcon[r.status]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
    </div>
  );
}

export default ImportUrlsPage;
